import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { paymentsAPI } from '../api/payments';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/formatter';
import toast from 'react-hot-toast';

const PER_PAGE = 10;

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // filters / search / pagination
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [range, setRange] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    paymentsAPI.getPaymentHistory()
      .then(data => {
        const list = Array.isArray(data) ? data : data?.payments || data?.data || [];
        setTransactions(list);
      })
      .catch(error => {
        console.error('Tx fetch error:', error);
        setTransactions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Reset to page 1 whenever filters or active tab change
  useEffect(() => { setPage(1); }, [search, methodFilter, range, sort, activeTab]);

  const tabMatchesStatus = (status) => {
    const s = (status || '').toLowerCase();
    switch (activeTab) {
      case 'completed': return s === 'completed' || s === 'success';
      case 'pending': return s === 'pending';
      case 'issues': return ['failed', 'cancelled', 'refunded'].includes(s);
      default: return true;
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const rangeMs = {
      all: Infinity,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
      '90d': 90 * 24 * 60 * 60 * 1000,
    }[range] ?? Infinity;

    let list = transactions.filter(t => {
      if (!tabMatchesStatus(t.status)) return false;
      if (methodFilter !== 'all') {
        const m = (t.method || t.paymentMethod || '').toLowerCase();
        if (!m.includes(methodFilter)) return false;
      }
      if (rangeMs !== Infinity && t.createdAt) {
        if (now - new Date(t.createdAt).getTime() > rangeMs) return false;
      }
      if (q) {
        const desc = (t.description || t.service?.name || '').toLowerCase();
        const id = (t.id || t.paymentId || '').toString().toLowerCase();
        const booking = (t.bookingId || '').toString().toLowerCase();
        const amount = (t.amount || 0).toString();
        if (!desc.includes(q) && !id.includes(q) && !booking.includes(q) && !amount.includes(q)) {
          return false;
        }
      }
      return true;
    });

    list.sort((a, b) => {
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      const aAmt = a.amount || 0;
      const bAmt = b.amount || 0;
      switch (sort) {
        case 'oldest': return aDate - bDate;
        case 'highest': return bAmt - aAmt;
        case 'lowest': return aAmt - bAmt;
        default: return bDate - aDate;
      }
    });

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, search, methodFilter, range, sort, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const totalSpent = useMemo(
    () => transactions
      .filter(t => ['completed', 'success'].includes((t.status || '').toLowerCase()))
      .reduce((sum, t) => sum + (t.amount || 0), 0),
    [transactions]
  );

  const filteredTotal = useMemo(
    () => filtered.reduce((sum, t) => sum + (t.amount || 0), 0),
    [filtered]
  );

  const counts = useMemo(() => {
    const c = { all: transactions.length, completed: 0, pending: 0, issues: 0 };
    transactions.forEach(t => {
      const s = (t.status || '').toLowerCase();
      if (s === 'completed' || s === 'success') c.completed++;
      else if (s === 'pending') c.pending++;
      else if (['failed', 'cancelled', 'refunded'].includes(s)) c.issues++;
    });
    return c;
  }, [transactions]);

  const hasActiveFilters = search || methodFilter !== 'all' || range !== 'all' || sort !== 'newest';

  const clearFilters = () => {
    setSearch('');
    setMethodFilter('all');
    setRange('all');
    setSort('newest');
  };

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const rows = [
      ['ID', 'Date', 'Description', 'Method', 'Amount', 'Status'],
      ...filtered.map(t => [
        t.id || t.paymentId || '',
        t.createdAt ? new Date(t.createdAt).toISOString() : '',
        (t.description || t.service?.name || t.bookingId || 'Payment').replace(/,/g, ' '),
        t.method || t.paymentMethod || '',
        t.amount || 0,
        t.status || '',
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Transactions exported');
  };

  const tabs = [
    { id: 'all', label: 'All', icon: ListBulletIcon, count: counts.all },
    { id: 'completed', label: 'Completed', icon: CheckCircleIcon, count: counts.completed },
    { id: 'pending', label: 'Pending', icon: ClockIcon, count: counts.pending },
    { id: 'issues', label: 'Issues', icon: ExclamationCircleIcon, count: counts.issues },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Transactions</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Browse, filter, and export your full payment history
          </p>
        </div>

        {/* Banner */}
        <div
          className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
          style={{ backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-5)' }}
        >
          <ShieldCheckIcon style={{ width: '20px', height: '20px', color: 'var(--orange-9)' }} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)' }}>
            Secure record of every booking payment, refund, and adjustment
          </span>
        </div>

        {/* Tabs */}
        <div
          className="flex gap-1 mb-8 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
        >
          {tabs.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon style={{ width: '16px', height: '16px' }} />
              {label}
              <span style={{
                fontSize: '11px', fontWeight: 600,
                padding: '1px 7px', borderRadius: '999px',
                backgroundColor: activeTab === id ? 'var(--orange-3)' : 'var(--gray-4)',
                color: activeTab === id ? 'var(--orange-11)' : 'var(--gray-11)',
              }}>
                {count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-10">
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                backgroundColor: 'var(--gray-3)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
              }}>
                <ClockIcon style={{ width: '26px', height: '26px', color: 'var(--gray-9)' }} />
              </div>
              <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 4px' }}>
                No transactions yet
              </p>
              <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 16px' }}>
                Your payment history will appear here once you book a service
              </p>
              <Link to="/search">
                <Button>Browse Services</Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                <div
                  className="rounded-lg p-4"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)' }}
                >
                  <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>Total Transactions</p>
                  <p style={{ fontSize: '22px', fontWeight: 700, color: 'var(--gray-12)', margin: '4px 0 0', textAlign: 'left' }}>
                    {transactions.length}
                  </p>
                </div>
                <div
                  className="rounded-lg p-4"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)' }}
                >
                  <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>Total Spent</p>
                  <p style={{ fontSize: '22px', fontWeight: 700, color: 'var(--orange-9)', margin: '4px 0 0', textAlign: 'left' }}>
                    {formatCurrency(totalSpent)}
                  </p>
                </div>
                <div
                  className="rounded-lg p-4"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)' }}
                >
                  <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>Filtered Total</p>
                  <p style={{ fontSize: '22px', fontWeight: 700, color: 'var(--gray-12)', margin: '4px 0 0', textAlign: 'left' }}>
                    {formatCurrency(filteredTotal)}
                  </p>
                </div>
              </div>

              {/* Search */}
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <MagnifyingGlassIcon style={{
                  position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                  width: '16px', height: '16px', color: 'var(--gray-9)', pointerEvents: 'none',
                }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by description, ID, or amount..."
                  style={{
                    width: '100%', padding: '10px 36px 10px 38px', borderRadius: '10px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                  }}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    style={{
                      position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                      padding: '4px', border: 'none', background: 'transparent', cursor: 'pointer',
                      display: 'flex',
                    }}
                  >
                    <XMarkIcon style={{ width: '16px', height: '16px', color: 'var(--gray-9)' }} />
                  </button>
                )}
              </div>

              {/* Filters row */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <select
                  value={methodFilter}
                  onChange={(e) => setMethodFilter(e.target.value)}
                  style={{
                    padding: '7px 10px', borderRadius: '8px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="all">All methods</option>
                  <option value="card">Card</option>
                  <option value="mobile">Mobile money</option>
                  <option value="cash">Cash</option>
                </select>

                <select
                  value={range}
                  onChange={(e) => setRange(e.target.value)}
                  style={{
                    padding: '7px 10px', borderRadius: '8px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="all">All time</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                </select>

                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  style={{
                    padding: '7px 10px', borderRadius: '8px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="highest">Highest amount</option>
                  <option value="lowest">Lowest amount</option>
                </select>

                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    style={{
                      padding: '7px 10px', borderRadius: '8px',
                      border: '1px solid var(--gray-5)', backgroundColor: 'transparent',
                      color: 'var(--gray-11)', fontSize: '13px', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    <XMarkIcon style={{ width: '14px', height: '14px' }} />
                    Clear
                  </button>
                )}

                <div style={{ flex: 1 }} />

                <button
                  type="button"
                  onClick={exportCsv}
                  disabled={filtered.length === 0}
                  style={{
                    padding: '7px 10px', borderRadius: '8px',
                    border: '1px solid var(--orange-6)', backgroundColor: 'transparent',
                    color: 'var(--orange-9)', fontSize: '13px', fontWeight: 600,
                    cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: filtered.length === 0 ? 0.5 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  <ArrowDownTrayIcon style={{ width: '14px', height: '14px' }} />
                  Export CSV
                </button>
              </div>

              {/* Result count */}
              <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '0 0 10px', textAlign: 'left' }}>
                Showing {filtered.length === 0 ? 0 : (page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
                {hasActiveFilters && ` (filtered)`}
              </p>

              {/* List */}
              {filtered.length === 0 ? (
                <div className="text-center py-10 rounded-lg" style={{ backgroundColor: 'var(--color-background)', border: '1px dashed var(--gray-5)' }}>
                  <p style={{ fontSize: '14px', color: 'var(--gray-10)', margin: 0 }}>
                    No transactions match your filters.
                  </p>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      style={{
                        marginTop: '8px', padding: '6px 14px', borderRadius: '8px',
                        border: '1px solid var(--orange-6)', backgroundColor: 'transparent',
                        color: 'var(--orange-9)', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {pageItems.map(t => {
                    const status = (t.status || '').toLowerCase();
                    const statusColor =
                      status === 'completed' || status === 'success' ? '#22c55e' :
                      status === 'pending' ? 'var(--orange-9)' :
                      status === 'failed' || status === 'cancelled' ? 'var(--red-9, #e5484d)' :
                      status === 'refunded' ? '#3b82f6' : 'var(--gray-10)';
                    const statusBg =
                      status === 'completed' || status === 'success' ? 'var(--green-3, #dcfce7)' :
                      status === 'pending' ? 'var(--orange-3)' :
                      status === 'failed' || status === 'cancelled' ? 'var(--red-3, #fee2e2)' :
                      status === 'refunded' ? '#dbeafe' : 'var(--gray-3)';
                    return (
                      <div
                        key={t.id || t.paymentId || `${t.createdAt}-${t.amount}`}
                        className="rounded-lg p-4 flex items-center justify-between"
                        style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)' }}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                            {t.description || t.service?.name || t.bookingId || 'Payment'}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '2px 0 0', textAlign: 'left' }}>
                            {t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}
                            {(t.method || t.paymentMethod) && ` · ${t.method || t.paymentMethod}`}
                            {(t.id || t.paymentId) && ` · #${(t.id || t.paymentId).toString().slice(-8).toUpperCase()}`}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                          <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gray-12)', margin: 0 }}>
                            {formatCurrency(t.amount || 0)}
                          </p>
                          <span
                            style={{
                              display: 'inline-block', marginTop: '4px',
                              padding: '2px 8px', borderRadius: '999px',
                              fontSize: '11px', fontWeight: 600,
                              backgroundColor: statusBg, color: statusColor,
                              textTransform: 'capitalize',
                            }}
                          >
                            {t.status || 'unknown'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {filtered.length > PER_PAGE && (
                <div className="flex items-center justify-between mt-5 pt-4" style={{ borderTop: '1px solid var(--gray-4)' }}>
                  <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px',
                        cursor: page === 1 ? 'default' : 'pointer',
                        backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                        opacity: page === 1 ? 0.4 : 1,
                      }}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                      .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) => p === '...' ? (
                        <span key={`gap-${i}`} style={{ padding: '0 6px', color: 'var(--gray-9)', fontSize: '13px', alignSelf: 'center' }}>…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          style={{
                            width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                            fontSize: '13px', fontWeight: page === p ? 600 : 400, cursor: 'pointer',
                            backgroundColor: page === p ? 'var(--orange-9)' : 'var(--gray-4)',
                            color: page === p ? '#fff' : 'var(--gray-11)',
                          }}
                        >
                          {p}
                        </button>
                      ))}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px',
                        cursor: page === totalPages ? 'default' : 'pointer',
                        backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                        opacity: page === totalPages ? 0.4 : 1,
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transactions;
